import { format } from "date-fns";
import { DATE_FORMATS, ERewardPenaltyStatusv4 } from "../constants";
import { IncentiveRecordDto } from "../interfaces/monthly-earnings";
import { isDefined, isNullOrUndefined } from "./common";
import { formatDateAndTime } from "./date";
import { pluralize } from "./pluralize";

const INCENTIVE_STATUS_LABELS: Partial<Record<ERewardPenaltyStatusv4, string>> = {
    [ERewardPenaltyStatusv4.REJECTED]: "Rejected",
    [ERewardPenaltyStatusv4.CANCELLED]: "Cancelled",
};

/** Aggregate incentives (weekly/monthly milestones) are not tied to a session. */
export const isAggregateIncentive = (record: IncentiveRecordDto): boolean => isNullOrUndefined(record.session);

/**
 * Label for the leading column of an incentive row.
 * Session-based incentives show when they were calculated; aggregate ones show the period they cover.
 */
export const getIncentivePeriodLabel = (record: IncentiveRecordDto): string => {
    const { periodStartsAt, periodEndsAt, calculatedAt } = record;

    if (isNullOrUndefined(periodStartsAt) || isNullOrUndefined(periodEndsAt)) {
        return formatDateAndTime(calculatedAt);
    }

    return `${format(new Date(periodStartsAt), DATE_FORMATS.DISPLAY_DATE)} - ${format(new Date(periodEndsAt), DATE_FORMATS.DISPLAY_DATE)}`;
};

/**
 * Short explanation of why an aggregate incentive was earned, derived from its evaluation metrics.
 * Falls back to the rule description when no known metric is present.
 */
export const getIncentiveEarnedNote = (record: IncentiveRecordDto): string | undefined => {
    const metrics = record.evaluationMetrics;
    const completedSessions = metrics?.completedSessions;

    if (typeof completedSessions === "number") {
        return `${pluralize(completedSessions, "session")} completed`;
    }

    return record.rule.description ?? undefined;
};

/** Status shown next to the amount. Approved incentives need no label. */
export const getIncentiveStatusLabel = (status?: ERewardPenaltyStatusv4 | null): string | undefined => {
    if (isNullOrUndefined(status) || status === ERewardPenaltyStatusv4.APPROVED) return undefined;

    if (status === ERewardPenaltyStatusv4.CALCULATED) return "Pending approval";

    return isDefined(INCENTIVE_STATUS_LABELS[status]) ? INCENTIVE_STATUS_LABELS[status] : undefined;
};
